import {
  AutocompleteArrayInput,
  Datagrid,
  DateField,
  DateTimeInput,
  Filter,
  FunctionField,
  Link,
  List,
  ReferenceArrayInput,
  SelectInput,
  ShowButton,
  TextField,
  TextInput,
  downloadCSV,
  useGetIdentity,
} from 'react-admin';
import { Booking, BookingStatus, UserRole } from '../../@generated/schemas';

import { Chip } from '@mui/material';
import CustomPagination from '../../CustomPagination';
import { ResponsiveList } from '../../common/ResponsiveList';
import dayjs from 'dayjs';
import formattedPrice from '../../libs/formattedPrice';
import jsonExport from 'jsonexport/dist';
import mapEnumSelectInput from '../../libs/mapEnumSelectInput';
import { sumBy } from 'lodash';
import { useTranslate } from '../../locales';

export const BookingFilter = (props: any) => {
  const { identity } = useGetIdentity();
  const translate = useTranslate();

  const defaultVehicleFilter = {
    deletedAt: {
      equals: null,
    },
    OR:
      identity?.userRole && identity?.userRole !== UserRole.SuperAdmin
        ? [
          {
            user:
              identity?.userRole && identity?.userRole !== UserRole.SuperAdmin
                ? {
                  is: {
                    companyId: {
                      equals: identity?.companyId,
                    },
                  },
                }
                : undefined,
          },
          {
            companyId: {
              equals: identity?.companyId,
            },
          },
        ]
        : [],
  };

  const defaultUserFilter = {
    deletedAt: {
      equals: null,
    },
    companyId:
      identity?.userRole && identity?.userRole !== UserRole.SuperAdmin
        ? { equals: identity?.companyId }
        : undefined,
  };

  return (
    <Filter {...props}>
      <TextInput label={translate('admin.search')} source="q" alwaysOn />
      <TextInput source="bookingHash" label={translate('admin.bookingHash')} />
      <DateTimeInput
        source="cancelDate_gte"
        label={translate('admin.cancelledFrom')}
      />
      <DateTimeInput
        source="cancelDate_lte"
        label={translate('admin.cancelledTo')}
      />
      <DateTimeInput
        source="adminChangeCommentAt_gte"
        label={translate('admin.adminChangedDateFrom')}
      />
      <SelectInput
        source="bookingStatus"
        translateChoice={false}
        label={translate('admin.bookingStatus')}
        choices={mapEnumSelectInput(BookingStatus)}
      />
      <ReferenceArrayInput
        source="vehicle_ids.is"
        reference="Vehicle"
        label={translate('admin.vehicles')}>
        <AutocompleteArrayInput
          label={translate('admin.vehicles')}
          optionText={(choice: any) =>
            `${choice.plateNumber} (${choice.name || choice?.vin})`
          }
          style={{ width: 300 }}
        />
      </ReferenceArrayInput>
      <ReferenceArrayInput
        source="user_ids.is.company_ids.is"
        reference="Company"
        label={translate('admin.companies')}>
        <AutocompleteArrayInput
          label={translate('admin.companies')}
          optionText={(choice: any) => `${choice.name}`}
          style={{ width: 300 }}
        />
      </ReferenceArrayInput>
      <ReferenceArrayInput
        source="user_ids.is"
        reference="User"
        label={translate('admin.users')}>
        <AutocompleteArrayInput
          label={translate('admin.users')}
          optionText={(choice: any) => choice?.email}
          style={{ width: 300 }}
        />
      </ReferenceArrayInput>
      <DateTimeInput
        source="startDate_gte"
        label={translate('admin.bookingStartDate_from')}
      />
      <DateTimeInput
        source="startDate_lte"
        label={translate('admin.bookingStartDate_to')}
      />
      <DateTimeInput
        source="endDate_gte"
        label={translate('admin.bookingEndDate_from')}
      />
      <DateTimeInput
        source="endDate_lte"
        label={translate('admin.bookingEndDate_to')}
      />
    </Filter>
  );
};

const formattedMileage = (record) => {
  return Number(
    (record.vehicleMileageOnEffectiveEnd ||
      record.vehicleMileageOnEffectiveStart ||
      0) -
    (record.vehicleMileageOnEffectiveStart ||
      record.vehicleMileageOnEffectiveEnd ||
      0),
  ).toFixed(1);
};

const customExporter = (items: Booking[]) => {
  const listForExport = items.map((row) => {
    const excessCost = row.calculation.endPrice?.costForDistance || 0;
    const additionalCost = row.calculation.endPrice?.lateFees || 0;
    // parseFloat(
    //   formattedPrice(
    //     (row.calculation?.endPrice?.totalCostWithoutInsurance || 0) -
    //       (row.totalCost ||
    //         row.calculation?.startPrice?.totalCostWithoutInsurance ||
    //         0),
    //     false,
    //   ),
    // ) - excessCost;

    const bookingCalculation =
      row.calculation.endPrice || row.calculation.startPrice;

    const refundedCost = sumBy(row.bookingRefunding, 'refunding') || 0;
    let reducedTotalCost =
      bookingCalculation.totalCostWithoutInsurance - refundedCost < 0
        ? 0
        : bookingCalculation.totalCostWithoutInsurance - refundedCost;

    if (row.cancelDate) {
      reducedTotalCost =
        row.cancellationFees - refundedCost < 0
          ? 0
          : row.cancellationFees - refundedCost;
    }

    const commision = reducedTotalCost * (row.commission / 100);

    return {
      'Booking hash': row.bookingHash,
      User: row.user.email,
      'User First name': row.user.firstName,
      'User Last name': row.user.lastName,
      'Vehicle VIN': row.vehicle.vin,
      'Vehicle Plate number': row.vehicle.plateNumber,
      'Start date': row.startDate
        ? dayjs(row.startDate).format('DD.MM. YYYY')
        : '',
      'Start time': row.startDate ? dayjs(row.startDate).format('HH:mm') : '',
      'End date': row.endDate ? dayjs(row.endDate).format('DD.MM. YYYY') : '',
      'End time': row.endDate ? dayjs(row.endDate).format('HH:mm') : '',
      'Effective start date': row.effectiveStartDate
        ? dayjs(row.effectiveStartDate).format('DD.MM. YYYY')
        : '',
      'Effective start time': row.effectiveStartDate
        ? dayjs(row.effectiveStartDate).format('HH:mm')
        : '',
      'Effective end date': row.effectiveEndDate
        ? dayjs(row.effectiveEndDate).format('DD.MM. YYYY')
        : '',
      'Effective end time': row.effectiveEndDate
        ? dayjs(row.effectiveEndDate).format('HH:mm')
        : '',
      'Start / end km': `${row.vehicleMileageOnEffectiveStart || 0}/${row.vehicleMileageOnEffectiveEnd || 0
        }`,
      'Driven km': Math.max(0, Number(formattedMileage(row))),
      'Excess km': row.calculation.endPrice?.excessKm,
      'Insurance Name': row.vehicle.vehicleInsuranceType?.name,
      'Subtotal rental costs':
        row.calculation?.endPrice?.totalCostWithoutInsurance ||
        row.calculation?.startPrice?.totalCostWithoutInsurance,
      'Insurance costs': row.calculation?.endPrice?.insuranceCost || 0,
      'Late fees': Math.max(0, excessCost > 0 || additionalCost > 0 ? additionalCost : 0),
      'Total costs': `${parseFloat(`${Math.max(0, row.effectiveBookingCost || 0)}`).toFixed(2)}`,
      Refunded: `${parseFloat(`${Math.max(0, refundedCost)}`).toFixed(2)}`,
      'Commision to owner': `${parseFloat(`${Math.max(0, commision)}`).toFixed(2)}`,
    };
  });

  jsonExport(listForExport, { rowDelimiter: ';' }, (err, csv) =>
    downloadCSV(csv, 'Booking report'),
  );
};

export const BookingList = (props: any) => {
  const { identity } = useGetIdentity();
  const translate = useTranslate();

  const defaultFilter = {
    cancelDate: true,
    OR: [
      {
        user: {
          is: {
            companyId: {
              equals: identity?.companyId,
            },
          },
        },
      },
      {
        vehicle: {
          is: {
            OR: [
              {
                user: {
                  is: {
                    companyId: {
                      equals: identity?.companyId,
                    },
                  },
                },
              },
              {
                companyId: {
                  equals: identity?.companyId,
                },
              },
            ],
          },
        },
      },
    ],
  };

  if (identity?.userRole && identity?.userRole === UserRole.SuperAdmin) {
    delete defaultFilter.OR;
  }

  return (
    <ResponsiveList
      listProps={{
        filter: defaultFilter,
        exporter: customExporter,
        filters: <BookingFilter />,
        sort: {
          field: 'createdAt',
          order: 'ASC'
        },
        pagination: <CustomPagination />
      }}
      datagridProps={{
        bulkActionButtons: false,
      }}>
      <TextField
        source="bookingHash"
        label={translate('admin.bookingHash')}
      />
      <FunctionField
        source="vehicle.vin"
        label={translate('admin.vin')}
        render={(row) => (
          <Link to={`/Vehicle/${row.vehicle.id}`}>{row.vehicle.vin}</Link>
        )}
      />
      <FunctionField
        source="vehicle.plateNumber"
        label={translate('admin.plateNumber')}
        render={(row) => (
          <Link to={`/Vehicle/${row.vehicle.id}`}>
            {row.vehicle.plateNumber}
          </Link>
        )}
      />
      <FunctionField
        source="vehicle.name"
        label={translate('admin.name')}
        render={(row) => (
          <Link to={`/Vehicle/${row.vehicle.id}`}>
            {row.vehicle.name}
          </Link>
        )}
      />
      <FunctionField
        source="startDate"
        label={translate('admin.bookingColumn')}
        render={(record) => {
          return (
            <div>
              <div>
                <b>{translate('admin.start')}:</b>
              </div>{' '}
              {dayjs(record.startDate).format('DD.MM. YYYY HH:mm')}
              <br />
              <div>
                <b>{translate('admin.end')}:</b>
              </div>{' '}
              {dayjs(record.endDate).format('DD.MM. YYYY HH:mm')}
            </div>
          );
        }}
      />
      <FunctionField
        source="effectiveStartDate"
        label={translate('admin.effective')}
        render={(record) => {
          return (
            <div>
              {record.effectiveStartDate && (
                <>
                  <div>
                    <b>{translate('admin.start')}:</b>
                  </div>{' '}
                  {dayjs(record.effectiveStartDate).format(
                    'DD.MM. YYYY HH:mm',
                  )}
                </>
              )}
              <br />
              {record.effectiveEndDate && (
                <>
                  <div>
                    <b>{translate('admin.end')}:</b>
                  </div>{' '}
                  {dayjs(record.effectiveEndDate).format('DD.MM. YYYY HH:mm')}
                </>
              )}
            </div>
          );
        }}
      />
      <FunctionField
        source="mileage"
        sortable={false}
        label={translate('admin.drivenKm')}
        render={(record) => formattedMileage(record)}
      />
      <FunctionField
        label={translate('admin.bookingStatus')}
        render={(record) => {
          return <Chip label={record.bookingStatus} color="primary" />;
        }}
      />
      <FunctionField
        source="user.email"
        label={translate('admin.emailField')}
        render={(row) => (
          <Link to={`/User/${row.user.id}`}>{row.user.email}</Link>
        )}
      />
      <DateField
        source="createdAt"
        label={translate('admin.createdAt')}
        lang="en"
        showTime
      />
      <FunctionField
        label={translate('admin.effectiveBookingCost')}
        source="effectiveBookingCost"
        render={(record) => formattedPrice(record.effectiveBookingCost || 0)}
      />
      {identity?.userRole === UserRole.SuperAdmin ? (
        <DateField
          source="stripeTransferedAt"
          label={translate('admin.stripeTransferedAt')}
          lang="en"
          showTime
        />
      ) : null}
      <ShowButton />
    </ResponsiveList>
  );
};
